import React, { useState, useMemo } from "react";
import "./activeCompsTab.css";
import MarketControlBar from "./MarketControlBar";
import MobileFiltersDrawer from "./MobileFiltersDrawer";
import SummaryStatsRow from "./SummaryStatsRow";
import CompRowCard from "./CompRowCard";
import VehicleSummaryCard from "./VehicleSummaryCard";

export default function ActiveCompsTab({ data, isMobile }) {
  if (!data) return null;

  const [selectedComp, setSelectedComp] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Default mileage max 500k so high-mileage comps within radius still show when no filters are selected
  const defaultFilters = {
    sortBy: "price",
    sortOrder: "asc",
    mileageRange: [0, 500000],
    similarMileage: false,
    radius: 100,
    independentDealersOnly: false,
    franchiseOnly: false,
    matchingVersionOnly: false,
    matchingTransmissionOnly: false,
    matchingDrivetrainOnly: false,
  };
  const [filters, setFilters] = useState(defaultFilters);

  const metrics = data?.market_analysis?.market_metrics || {};
  const vehicleInfo = data?.vehicle_info || {};
  const pricingInfo = data?.pricing_info || {};
  const searchMetadata = data?.search_metadata || {};
  const hooptieMileage = searchMetadata?.mileage || vehicleInfo?.mileage || 0;
  const hooptiePrice = pricingInfo?.marketcheck_price || pricingInfo?.hooptie_price || searchMetadata?.asking_price || 0;
  // Exact vehicle drivetrain/transmission from vehicle_info (with fallbacks for alternate API keys)
  const hooptieDrivetrain = vehicleInfo?.drivetrain ?? vehicleInfo?.drivetrain_type ?? searchMetadata?.drivetrain ?? null;
  const hooptieTransmission = vehicleInfo?.transmission ?? vehicleInfo?.transmission_type ?? searchMetadata?.transmission ?? null;

  const activeListings = data?.active_listings || data?.market_analysis?.active_listings || [];

  // Transform active_listings into comp format (include listings without price or mileage)
  const allComps = useMemo(() => {
    return activeListings.map((listing, index) => {
        const listingPrice = listing.price ?? null;
        const listingMiles = listing.miles ?? null;
        // Compare against Hooptie's price (use 0 delta when listing has no price)
        const priceDelta = listingPrice != null ? listingPrice - hooptiePrice : 0;
        const priceDeltaType = priceDelta < -500 ? "under" : priceDelta > 500 ? "over" : "market";
        // Compare against Hooptie's mileage (use 0 delta when listing has no mileage)
        const mileageDelta = listingMiles != null ? listingMiles - hooptieMileage : 0;
        const mileageDeltaType = mileageDelta > 0 ? "over" : "under";
        const avgDom = metrics.avg_days_on_market || 0;
        const domDelta = avgDom - (listing.dom_active || 0);
        const domDeltaType = domDelta > 0 ? "faster" : "slower";
        
        // Determine status
        let status = "Active";
        if (listingPrice != null && priceDelta > 1000 && (listing.dom_active || 0) > avgDom) {
          status = "Overpriced";
        }
        
        // Calculate comp quality (1-5); use safe defaults when price or mileage missing
        const avgActivePrice = metrics.avg_active_price || hooptiePrice || 1;
        const avgActiveMileage = metrics.avg_active_mileage || hooptieMileage || 1;
        const priceDiffPercent = listingPrice != null ? Math.abs(priceDelta) / avgActivePrice : 0;
        const mileageDiffPercent = listingMiles != null ? Math.abs(mileageDelta) / avgActiveMileage : 0;
        const domDiffPercent = (metrics.avg_days_on_market && metrics.avg_days_on_market > 0) ? Math.abs(domDelta) / metrics.avg_days_on_market : 0;
        const totalDiff = (priceDiffPercent + mileageDiffPercent + domDiffPercent) / 3;
        let compQuality = 5;
        if (totalDiff > 0.3) compQuality = 1;
        else if (totalDiff > 0.2) compQuality = 2;
        else if (totalDiff > 0.1) compQuality = 3;
        else if (totalDiff > 0.05) compQuality = 4;
        
        const qualityLevel = compQuality <= 2 ? "LOW" : compQuality <= 3 ? "MED" : "HIGH";
        
        return {
          id: listing.vin || `comp-${index}`,
          image: listing.photo || null, // Only use listing photo; show "Image not available" when missing or load fails
          title: listing.heading || `${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model} ${vehicleInfo.trim}`,
          price: listing.price ?? null,
          dealer: listing.dealer_name,
          priceDelta: Math.abs(priceDelta),
          priceDeltaType,
          mileageDelta: Math.abs(mileageDelta),
          mileageDeltaType,
          domDelta: Math.abs(domDelta),
          domDeltaType,
          status,
          avgPrice: Math.round(metrics.avg_active_price),
          avgDom: metrics.avg_days_on_market,
          compQuality,
          qualityLevel,
          dom: listing.dom_active,
          mileage: listing.miles ?? listing.mileage ?? null,
          profit: (metrics?.avg_active_price != null && listing.price != null) ? Math.round(metrics.avg_active_price - listing.price) : null,
          priceBand: (metrics?.price_range_active?.min != null && metrics?.price_range_active?.max != null)
            ? `$${(metrics.price_range_active.min / 1000).toFixed(1)}k - $${(metrics.price_range_active.max / 1000).toFixed(1)}k`
            : '—',
          vin: listing.vin,
          dealer_type: listing.dealer_type,
          version: listing.version,
          url: listing.vdp_url,
          distance_miles: listing.distance_miles ?? listing.distance_mi ?? listing.distance ?? null,
          city: listing.city ?? listing.dealer_city ?? null,
          state: listing.state ?? listing.dealer_state ?? null,
          transmission: listing.transmission ?? null,
          drivetrain: listing.drivetrain ?? null,
        };
      });
  }, [metrics, vehicleInfo, hooptiePrice, hooptieMileage, activeListings]);

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => {
      const merged = { ...prev, ...newFilters };
      // MarketControlBar / drawer already set mileageRange when enabling similar mileage
      if (merged.similarMileage && !prev.similarMileage) {
        return merged;
      }
      if (!merged.similarMileage && prev.similarMileage) {
        const allMileages = allComps.map(comp => comp.mileage).filter(m => m != null && m > 0);
        const minMileageBound = allMileages.length > 0 ? Math.min(...allMileages) : 0;
        const maxMileageBound = allMileages.length > 0 ? Math.max(...allMileages, 500000) : 500000;
        return { ...merged, mileageRange: [minMileageBound, maxMileageBound] };
      }
      return merged;
    });
  };

  const handleCompSelect = (comp) => {
    setSelectedComp({
      title: comp.title,
      image: comp.image,
      price: comp.price,
      mileage: comp.mileage,
      vin: comp.vin,
      dom: comp.dom,
      dealer: comp.dealer,
      profit: comp.profit,
      avgDom: comp.avgDom,
      avgPrice: comp.avgPrice,
      priceBand: comp.priceBand
    });
  };

  // Apply filters and sorting
  const filteredComps = useMemo(() => {
    let filtered = [...allComps];

    const radiusMi = filters.radius != null ? filters.radius : 100;
    filtered = filtered.filter(comp => {
      const d = comp.distance_miles;
      return d == null || d <= radiusMi;
    });

    // Similar mileage filter - uses mileageRange set by control bar (based on selected ±%); include comps with no mileage
    if (filters.similarMileage) {
      const [minMileage, maxMileage] = filters.mileageRange;
      filtered = filtered.filter(comp => {
        const compMileage = comp.mileage;
        if (compMileage == null) return true;
        return compMileage >= minMileage && compMileage <= maxMileage;
      });
    } else {
      // Regular mileage filter (only apply if similar mileage is not active); include comps with no mileage
      filtered = filtered.filter(comp => {
        const compMileage = comp.mileage;
        if (compMileage == null) return true;
        const [min, max] = filters.mileageRange;
        return compMileage >= min && compMileage <= max;
      });
    }

    // Dealer type filter
    if (filters.independentDealersOnly) {
      filtered = filtered.filter(comp => comp.dealer_type === "independent");
    }
    if (filters.franchiseOnly) {
      filtered = filtered.filter(comp => comp.dealer_type === "franchise");
    }

    // Matching Version Only filter
    if (filters.matchingVersionOnly) {
      filtered = filtered.filter(comp => comp.version === vehicleInfo.version);
    }

    // Matching Transmission Only filter — compare against exact vehicle's transmission from vehicle_info
    if (filters.matchingTransmissionOnly && hooptieTransmission) {
      const target = String(hooptieTransmission).trim().toLowerCase();
      filtered = filtered.filter(comp => comp.transmission && String(comp.transmission).trim().toLowerCase() === target);
    }

    // Matching Drivetrain Only filter — compare against exact vehicle's drivetrain from vehicle_info
    if (filters.matchingDrivetrainOnly && hooptieDrivetrain) {
      const target = String(hooptieDrivetrain).trim().toLowerCase();
      filtered = filtered.filter(comp => comp.drivetrain && String(comp.drivetrain).trim().toLowerCase() === target);
    }

    // Sorting (put comps with missing value at end)
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (filters.sortBy) {
        case "price":
          aVal = a.price;
          bVal = b.price;
          break;
        case "mileage":
          aVal = a.mileage;
          bVal = b.mileage;
          break;
        case "distance":
          aVal = a.distance_miles;
          bVal = b.distance_miles;
          break;
        default:
          return 0;
      }
      const pushMissingToEnd = filters.sortOrder === "asc" ? Infinity : -Infinity;
      const hasVal = (v) => v != null && (filters.sortBy === "distance" ? true : v > 0);
      const aNum = hasVal(aVal) ? Number(aVal) : pushMissingToEnd;
      const bNum = hasVal(bVal) ? Number(bVal) : pushMissingToEnd;
      if (filters.sortOrder === "asc") {
        return aNum - bNum;
      } else {
        return bNum - aNum;
      }
    });

    return filtered;
  }, [allComps, filters, vehicleInfo, hooptieMileage, hooptieDrivetrain, hooptieTransmission]);

  // Calculate stats from filtered comps; exclude missing price/mileage from averages
  const summaryStats = React.useMemo(() => {
    if (filteredComps.length === 0) {
      return {
        activeListings: 0,
        avgPrice: 0,
        avgMileage: 0
      };
    }

    const compsWithPrice = filteredComps.filter(comp => comp.price != null && comp.price > 0);
    const compsWithMileage = filteredComps.filter(comp => comp.mileage != null && comp.mileage > 0);
    const totalPrice = compsWithPrice.reduce((sum, comp) => sum + comp.price, 0);
    const totalMileage = compsWithMileage.reduce((sum, comp) => sum + comp.mileage, 0);
    const avgPrice = compsWithPrice.length > 0 ? Math.round(totalPrice / compsWithPrice.length) : 0;
    const avgMileage = compsWithMileage.length > 0 ? Math.round(totalMileage / compsWithMileage.length) : 0;

    return {
      activeListings: filteredComps.length,
      avgPrice: avgPrice,
      avgMileage: avgMileage
    };
  }, [filteredComps]);

  const activeFilterCount = [filters.similarMileage, filters.independentDealersOnly, filters.franchiseOnly, filters.matchingVersionOnly, filters.matchingTransmissionOnly, filters.matchingDrivetrainOnly].filter(Boolean).length;
  const sortLabel = filters.sortBy === "price" ? "Price" : filters.sortBy === "mileage" ? "Mileage" : filters.sortBy === "distance" ? "Distance" : "Price";
  const sortArrow = filters.sortOrder === "asc" ? "↑" : "↓";

  if (isMobile) {
    return (
      <div className="active-comps-tab active-comps-tab-mobile">
        <div className="act-mobile-pills">
          <button type="button" className="act-mobile-pill act-mobile-pill-filters" onClick={() => setMobileFiltersOpen(true)}>
            Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
          </button>
          <button type="button" className="act-mobile-pill act-mobile-pill-sort" onClick={() => handleFilterChange({ ...filters, sortOrder: filters.sortOrder === "asc" ? "desc" : "asc" })}>
            Sort: {sortLabel} {sortArrow}
          </button>
        </div>
        <MobileFiltersDrawer
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          hooptieMileage={hooptieMileage}
          filteredCount={summaryStats.activeListings}
          defaultFilters={defaultFilters}
        />
        <div className="act-mobile-summary-card">
          <div className="act-mobile-summary-price">${summaryStats.avgPrice.toLocaleString()}</div>
          <div className="act-mobile-summary-left">
            <div className="act-mobile-summary-title">ACTIVE LISTINGS</div>
            <div className="act-mobile-summary-count">{summaryStats.activeListings} Listings in Radius</div>
          </div>
        </div>
        <div className="act-mobile-comps-list">
          {filteredComps.length > 0 ? (
            filteredComps.map((comp) => (
              <CompRowCard
                key={comp.id}
                comp={comp}
                isSelected={selectedComp?.id === comp.id}
                onClick={() => handleCompSelect(comp)}
                isMobile={true}
                compactHorizontal={true}
              />
            ))
          ) : (
            <div className="no-comps-message">No comparable vehicles found matching your criteria.</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="active-comps-tab">
      <div className="act-grid">
        {/* Left Column (70%) */}
        <div className="act-left-column">
          <MarketControlBar onFilterChange={handleFilterChange} initialFilters={filters} hooptieMileage={hooptieMileage} />
          <SummaryStatsRow stats={summaryStats} />
          <div className="act-comps-card">
            {filteredComps.length > 0 ? (
              filteredComps.map((comp) => (
                <CompRowCard
                  key={comp.id}
                  comp={comp}
                  isSelected={selectedComp?.id === comp.id}
                  onClick={() => handleCompSelect(comp)}
                />
              ))
            ) : (
              <div className="no-comps-message">No comparable vehicles found matching your criteria.</div>
            )}
          </div>
        </div>
        <div className="act-right-column">
          <VehicleSummaryCard vehicle={selectedComp} data={data} />
        </div>
      </div>
    </div>
  );
}

